import React, { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'
import { Button } from './components/button'
import { Header } from './components/header'
import { Image } from './components/image'
import { Item } from './components/item'
import { Radio } from './components/radio'
import { Select } from './components/select'
import { Thumbnail } from './components/thumbnail'

export type Option = {
  id: number
  title: string
  image: string
}

export type Product = {
  id: number
  meta_img: string
  handle: string
  featured_image: string
  tags: string[]
}

export const Input = z.object({
  header: z.string().nullable(),
  cta: z.string().nullable(),
  collection_products: z.array(
    z.object({
      id: z.number(),
      meta_img: z.string(),
      handle: z.string(),
      featured_image: z.string(),
      tags: z.array(z.string())
    })
  ),
  floor: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      image: z.string()
    })
  ),
  wall: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      image: z.string()
    })
  ),
  ceiling: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      image: z.string()
    })
  ),
  pattern: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      image: z.string(),
      value: z.string()
    })
  )
})

export type Input = z.infer<typeof Input>

export const App = () => {
  const [input] = useState<Input>(() => {
    const inputContainer = document.getElementById('visualizer-data')
    return Input.parse(
      JSON.parse(inputContainer !== null ? inputContainer.innerText : '{}')
    )
  })

  const {
    cta,
    collection_products: collection,
    ceiling,
    floor,
    header,
    pattern: design,
    wall
  } = input

  const installingOptions = [
    {
      name: 'floor',
      id: 0
    },
    {
      name: 'wall',
      id: 1
    },
    {
      name: 'ceiling',
      id: 2
    }
  ]

  // VARIABLES
  const [activeArea, setArea] = useState<number>(0)
  const [activeBgImage, setBgImage] = useState<string | null>(null)
  const [activeAreaPattern, setAreaPattern] = useState<number>(0)
  const [activeDesign, setDesign] = useState<number>(0)
  const [activeOptions, setOptions] = useState<Option[]>(floor)
  const [activeOptionsLabel, setOptionsLabel] = useState<string>('Flooring')
  const [filteredCollection, setFilteredCollection] = useState(collection)
  const [activeThumbnail, setThumbnail] = useState<number>(0)
  const [itemImage, setItemImage] = useState<string | null>(null)
  const [product, setProduct] = useState<Product | null>(null)

  const changeArea = useCallback((e: number) => {
    setArea(e)
  }, [])

  const changeDesign = useCallback(
    (e: number) => {
      setDesign(design[e]?.id)
    },
    [design]
  )

  const changeThumbnailInfo = useCallback((e: number) => {
    setThumbnail(e)
  }, [])

  const buyProduct = useCallback(() => {
    if (product == null) {
      return
    }

    window.location.replace('/products/' + product.handle)
  }, [product])

  useEffect(() => {
    switch (activeArea) {
      case 0:
        setOptions(floor)
        setOptionsLabel('Flooring')
        break
      case 1:
        setOptions(wall)
        setOptionsLabel('Wall')
        break
      case 2:
        setOptions(ceiling)
        setOptionsLabel('Ceiling')
        break
      default:
        setOptions(floor)
        setOptionsLabel('Flooring')
        break
    }
  }, [activeArea, ceiling, floor, wall])

  useEffect(() => {
    setBgImage(activeOptions[0]?.image)
    setAreaPattern(0)
  }, [activeOptions])

  useEffect(() => {
    setBgImage(activeOptions[activeAreaPattern]?.image)
  }, [activeAreaPattern, activeOptions])

  useEffect(() => {
    setFilteredCollection(() => {
      const activeDesignTag = (design[activeDesign]?.value || '').toLowerCase()

      if (!activeDesignTag) {
        return collection
      }

      return collection.filter(function (item) {
        return item['tags'].find((tag) => {
          return tag.toLowerCase() === activeDesignTag
        })
      })
    })

    setThumbnail(0)
  }, [activeDesign, collection, design])

  useEffect(() => {
    const prod = collection.find(function (item) {
      return item['id'] === activeThumbnail
    })
    setItemImage(prod?.meta_img || null)
    setProduct(prod || null)
  }, [activeThumbnail, collection])

  return (
    <section className="Visualizer">
      {/* IMAGE */}
      <div className="Visualizer-imageBlock">
        <div className="Visualizer-mobile">
          <Header header={header} />
        </div>
        <Image itemImage={itemImage} backgroundImage={activeBgImage} />
        <div className="Visualizer-desktop">
          <Button text={cta} clickButton={buyProduct} />
        </div>
      </div>

      {/* FILTERS */}
      <div className="Visualizer-filtersBlock">
        <div className="Visualizer-desktop">
          <Header header={header} />
        </div>
        <Radio
          options={installingOptions}
          label="Where are You Installing?"
          onRadioChange={changeArea}
        />

        <Select
          label={activeOptionsLabel}
          options={activeOptions}
          selected={activeAreaPattern}
          onSelectChange={(e) => setAreaPattern(e)}
        />

        <Select
          label="Design / Pattern"
          options={design}
          selected={activeDesign}
          onSelectChange={changeDesign}
        />

        <div className="Visualizer-thumbnails">
          <label className="Visualizer-label">Select Finish Color</label>
          <div className="Visualizer-thumbnailGroup">
            {filteredCollection.length > 0 &&
              filteredCollection.map((item, i) => (
                <Thumbnail
                  item={item}
                  key={i}
                  active={activeThumbnail}
                  onThumbnailChange={changeThumbnailInfo}
                />
              ))}
          </div>
        </div>
      </div>

      {/* Mobile bar */}
      <div className="Visualizer-bar">
        <div>
          <Item image={itemImage} product={product} />
        </div>
        <div>
          <Button text={cta} clickButton={buyProduct} />
        </div>
      </div>
    </section>
  )
}
export default App
